import React, { useState, useEffect } from 'react';
import { IonIcon, IonSpinner } from '@ionic/react';
import { 
  chevronDownOutline, 
  chevronForwardOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  timeOutline,
  removeCircleOutline
} from 'ionicons/icons';
import './ExecutionPipeline.css';

/**
 * Process logs to handle carriage returns (used by git progress)
 * Carriage returns (\r) overwrite the current line in terminal
 */
const processLogs = (logs) => {
  if (!logs) return [];
  
  // Split by newlines, then process each line for carriage returns
  const lines = logs.split('\n');
  const processedLines = lines.map(line => {
    // If line contains \r, only keep the last segment (final state)
    if (line.includes('\r')) {
      const segments = line.split('\r');
      return segments[segments.length - 1];
    }
    return line;
  });
  
  // Filter out empty lines that were just progress updates
  return processedLines.filter(line => line.trim() !== '');
};

/**
 * Render logs with line numbers like GitLab.
 *
 * Placeholder messages reflect status truthfully — "Running..." was
 * misleading on completed runs where logs were never recorded (older
 * executions predating the artifact-upload pipeline, or runs where
 * upload-record failed silently). Pass `stepStatus` so the empty
 * state can say something accurate.
 */
const LogsWithLineNumbers = ({ logs, stepStatus }) => {
  const lines = processLogs(logs);

  if (!lines || lines.length === 0) {
    const terminal = ['success', 'completed', 'succeeded', 'failed', 'cancelled', 'skipped'].includes(stepStatus);
    if (terminal) {
      return <span className="log-placeholder">No logs recorded for this step.</span>;
    }
    return <span className="log-placeholder">Running...</span>;
  }

  return (
    <div className="log-lines">
      {lines.map((line, index) => (
        <div key={index} className="log-line">
          <span className="line-number">{index + 1}</span>
          <span className="line-content">{line}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * Reusable pipeline component for displaying execution steps
 * Used by both ImplementationDetailsPage and AnalysisDetailsPage
 */
const ExecutionPipeline = ({ steps = [], executionStatus = null }) => {
  const [expandedSteps, setExpandedSteps] = useState({});
  const [fetchedLogs, setFetchedLogs] = useState({});
  const [loadingLogs, setLoadingLogs] = useState({});
  const autoExpandedRef = React.useRef(new Set());
  const lastFetchTimeRef = React.useRef({});

  useEffect(() => {
    const runningIndex = steps.findIndex(s => s.status === 'running' || s.status === 'in_progress');
    
    if (runningIndex >= 0 && !autoExpandedRef.current.has(runningIndex)) {
      setExpandedSteps(prev => ({
        ...prev,
        [runningIndex]: true
      }));
      autoExpandedRef.current.add(runningIndex);
      const step = steps[runningIndex];
      if (step.logsUrl && !step.logs && !fetchedLogs[step.name]) {
        fetchLogsFromUrl(step.name, step.logsUrl);
      }
    }
  }, [steps]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch logs for expanded running steps, throttled to every 3s per step
  useEffect(() => {
    const now = Date.now();
    steps.forEach((step, index) => {
      if (expandedSteps[index] && step.logsUrl && (step.status === 'running' || step.status === 'in_progress')) {
        const lastFetch = lastFetchTimeRef.current[step.name] || 0;
        if (now - lastFetch > 3000) {
          lastFetchTimeRef.current[step.name] = now;
          fetchLogsFromUrl(step.name, step.logsUrl, true);
        }
      }
    });
  }, [steps]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLogsFromUrl = async (stepName, logsUrl, force = false) => {
    if ((!force && fetchedLogs[stepName]) || loadingLogs[stepName]) return;
    const isFirstFetch = !fetchedLogs[stepName];
    if (isFirstFetch) {
      setLoadingLogs(prev => ({ ...prev, [stepName]: true }));
    }
    try {
      const res = await fetch(logsUrl);
      if (res.ok) {
        const text = await res.text();
        setFetchedLogs(prev => ({ ...prev, [stepName]: text }));
      }
    } catch (err) {
      console.error(`Failed to fetch logs for ${stepName}:`, err);
    } finally {
      if (isFirstFetch) {
        setLoadingLogs(prev => ({ ...prev, [stepName]: false }));
      }
    }
  };

  const toggleStep = (stepIndex) => {
    const step = steps[stepIndex];
    const willExpand = !expandedSteps[stepIndex];
    if (willExpand && step.logsUrl && !step.logs && !fetchedLogs[step.name]) {
      fetchLogsFromUrl(step.name, step.logsUrl);
    }
    setExpandedSteps(prev => ({
      ...prev,
      [stepIndex]: willExpand
    }));
  };

  const getStatusIcon = (status) => {
    // If execution is cancelled, don't show spinner even if step says "running"
    if (executionStatus === 'cancelled' && (status === 'running' || status === 'in_progress')) {
      return <IonIcon icon={closeCircleOutline} style={{ color: '#ff9800', fontSize: '20px' }} />;
    }
    // If execution failed, don't show spinner for running steps
    if (executionStatus === 'failed' && (status === 'running' || status === 'in_progress')) {
      return <IonIcon icon={closeCircleOutline} style={{ color: '#ff4d4f', fontSize: '20px' }} />;
    }
    
    switch (status) {
      case 'success':
      case 'completed':
        return <IonIcon icon={checkmarkCircleOutline} style={{ color: '#52c41a', fontSize: '20px' }} />;
      case 'failed':
      case 'error':
        return <IonIcon icon={closeCircleOutline} style={{ color: '#ff4d4f', fontSize: '20px' }} />;
      case 'running':
      case 'in_progress':
        return <IonSpinner style={{ width: '18px', height: '18px' }} />;
      case 'cancelled':
        return <IonIcon icon={closeCircleOutline} style={{ color: '#ff9800', fontSize: '20px' }} />;
      case 'skipped':
        return <IonIcon icon={removeCircleOutline} style={{ color: '#8c8c8c', fontSize: '20px' }} />;
      default:
        return <IonIcon icon={timeOutline} style={{ color: '#8c8c8c', fontSize: '20px' }} />;
    }
  };

  return (
    <div className="execution-pipeline">
      {steps.map((step, index) => {
        // Show expand icon for running/completed/failed steps (even if no logs yet)
        const isExpandable = step.status !== 'queued' && step.status !== 'pending';
        
        return (
          <div key={index} className="pipeline-step">
            <div 
              className="step-header"
              onClick={() => isExpandable && toggleStep(index)}
              style={{ cursor: isExpandable ? 'pointer' : 'default' }}
            >
              <div className="step-left">
                {isExpandable && (
                  <IonIcon 
                    icon={expandedSteps[index] ? chevronDownOutline : chevronForwardOutline}
                    className="step-expand-icon"
                  />
                )}
                {getStatusIcon(step.status)}
                <span className="step-name">{step.displayName || step.name}</span>
              </div>
              <div className="step-right">
                <span className="step-duration">{step.duration}</span>
              </div>
            </div>
            
            {expandedSteps[index] && isExpandable && (
              <div className="step-logs">
                {loadingLogs[step.name] ? (
                  <span className="log-placeholder">Loading logs...</span>
                ) : (
                  <LogsWithLineNumbers
                    logs={step.logs || fetchedLogs[step.name]}
                    stepStatus={step.status}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ExecutionPipeline;
